<template>
    <div class="w-full">
        <div class="flex items-center space-x-3">
            <div class="flex-1 relative">
                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <font-awesome-icon :icon="['fas', 'search']" class="h-4 w-4 text-gray-400" />
                </div>
                <input type="text" v-model="basicSearchText" placeholder="Search by Name, Email or Phone Number"
                    class="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    @input="onBasicSearch" />
            </div>
            <button @click="openAdvancedSearchModal"
                class="flex items-center px-4 py-2.5 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                <font-awesome-icon :icon="['fas', 'sliders-h']" class="h-4 w-4 mr-2" />
                Advanced Search
            </button>
        </div>

        <transition name="modal">
            <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
                    <div
                        class="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center">
                                    <font-awesome-icon :icon="['fas', 'search-plus']" class="h-5 w-5 text-white mr-3" />
                                    <h3 class="text-lg font-semibold text-white">Advanced User Search</h3>
                                </div>
                                <button @click="closeModal"
                                    class="text-white hover:text-gray-200 focus:outline-none focus:text-gray-200 transition-colors duration-200">
                                    <font-awesome-icon :icon="['fas', 'times']" class="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div class="bg-white px-6 py-6">
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <!-- Role Filter -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                    <select v-model="advancedSearch.role"
                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option value="">All Roles</option>
                                        <option v-for="role in roles" :key="role.name" :value="role.name">
                                            {{ role.display_name || role.name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Entity Filter -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Entity</label>
                                    <select v-model="advancedSearch.entity"
                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                        <option value="">All Entities</option>
                                        <option v-for="entity in entities" :key="entity.value" :value="entity.value">
                                            {{ entity.label }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Campus Filter -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-2">Campus</label>
                                    <input v-model="advancedSearch.campus" type="text" placeholder="Enter campus"
                                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                                </div>
                            </div>
                        </div>

                        <div class="bg-gray-50 px-6 py-4 flex justify-between items-center">
                            <div class="flex items-center text-sm text-gray-500">
                                <font-awesome-icon :icon="['fas', 'info-circle']" class="h-4 w-4 mr-2" />
                                Use any combination of fields to refine your search
                            </div>
                            <div class="flex space-x-3">
                                <button @click="clearAdvancedSearch"
                                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200">
                                    Clear All
                                </button>
                                <button @click="applyAdvancedSearch"
                                    class="px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200">
                                    Apply Search
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "UserManagementAdvancedSearch",
    props: {
        initialBasicSearch: {
            type: String,
            default: ""
        },
        initialAdvancedSearch: {
            type: Object,
            default: () => ({})
        },
        roles: {
            type: Array,
            default: () => []
        },
        entities: {
            type: Array,
            default: () => []
        }
    },
    emits: ["on-basic-search", "on-advanced-search", "on-clear-search"],
    data() {
        return {
            showModal: false,
            basicSearchText: this.initialBasicSearch,
            advancedSearch: {
                role: "",
                entity: "",
                campus: "",
                ...this.initialAdvancedSearch
            }
        };
    },
    watch: {
        initialBasicSearch(newVal) {
            this.basicSearchText = newVal;
        },
        initialAdvancedSearch: {
            handler(newVal) {
                this.advancedSearch = { ...this.advancedSearch, ...newVal };
            },
            deep: true
        }
    },
    methods: {
        openAdvancedSearchModal() {
            this.showModal = true;
            document.body.style.overflow = 'hidden';
        },
        closeModal() {
            this.showModal = false;
            document.body.style.overflow = 'auto';
        },
        onBasicSearch() {
            this.$emit("on-basic-search", this.basicSearchText);
        },
        applyAdvancedSearch() {
            this.$emit("on-advanced-search", this.advancedSearch);
            this.closeModal();
        },
        clearAdvancedSearch() {
            this.advancedSearch = {
                role: "",
                entity: "",
                campus: "",
            };
            this.$emit("on-advanced-search", this.advancedSearch);
        },
        clearAllSearch() {
            this.basicSearchText = "";
            this.clearAdvancedSearch();
            this.$emit("on-basic-search", "");
            this.$emit("on-clear-search");
        }
    },
    beforeUnmount() {
        document.body.style.overflow = 'auto';
    }
}
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .inline-block,
.modal-leave-active .inline-block {
    transition: transform 0.3s ease;
}

.modal-enter-from .inline-block,
.modal-leave-to .inline-block {
    transform: scale(0.95) translateY(-20px);
}
</style>